import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TeacherPayrollRecord } from '@/hooks/useTeacherPayroll';
import { useGenerateTeacherSlip } from '@/hooks/useGenerateTeacherSlip';
import { useSendSlipEmail } from '@/hooks/useSendSlipEmail';
import { useGetFsTeacherSlipUrl } from '@/hooks/useGetFsTeacherSlipUrl';
import { 
  FileText, 
  Download, 
  Mail, 
  RefreshCw, 
  Loader2,
  CheckCircle,
  XCircle
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { format } from 'date-fns';

interface GenerateSlipDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  salary: TeacherPayrollRecord;
  onSuccess?: () => void;
}

export function GenerateSlipDialog({
  open,
  onOpenChange,
  salary,
  onSuccess
}: GenerateSlipDialogProps) {
  const [paidDate, setPaidDate] = useState(() => {
    // Default to last day of the salary month
    const year = salary.year;
    const month = salary.month;
    const lastDay = new Date(year, month, 0).getDate();
    return format(new Date(year, month - 1, lastDay), 'dd-MM-yyyy');
  });
  
  const { generateSlip, isGenerating } = useGenerateTeacherSlip();
  const { sendSlipEmail, isSending } = useSendSlipEmail();
  const { downloadFile, isLoading: isDownloading } = useGetFsTeacherSlipUrl();

  const hasSlip = !!salary.slip_url;
  const hasTavi50 = !!salary.tavi_50_url;
  const hasAnyFile = hasSlip || hasTavi50;

  const handleGenerate = async () => {
    const result = await generateSlip(salary, paidDate);
    if (result.success && onSuccess) {
      onSuccess();
    }
  };

  const handleSendEmail = async () => {
    if (!salary.id) {
      return;
    }
    await sendSlipEmail(salary.id);
  };

  const handleDownload = (fileType: 'slip' | 'tavi50') => {
    if (!salary.id) return;
    downloadFile(salary.id, fileType);
  };

  const formatThaiMonth = (month: number, year: number) => {
    const thaiMonths = [
      'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
      'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
    ];
    return `${thaiMonths[month - 1]} ${year + 543}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            สร้างสลิปเงินเดือน
          </DialogTitle>
          <DialogDescription>
            จัดการสลิปเงินเดือนและทวิ 50
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Teacher Info */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">ครู:</span>
              <span className="font-medium">
                {salary['ชื่อจริง-นามสกุล'] || salary.ชื่อเล่น} ({salary.รหัส})
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">เดือน:</span>
              <span className="font-medium">
                {formatThaiMonth(salary.month, salary.year)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">เงินสุทธิ:</span>
              <span className="font-medium text-primary">
                {salary.เงินสุทธิรวมประกัน?.toLocaleString('th-TH', { 
                  minimumFractionDigits: 2 
                })} บาท
              </span>
            </div>
          </div>

          {/* File Status */}
          <div className="border rounded-lg p-4 space-y-2">
            <h4 className="font-medium text-sm mb-2">สถานะไฟล์</h4>
            <div className="flex items-center gap-2">
              {hasSlip ? (
                <CheckCircle className="h-4 w-4 text-green-500" />
              ) : (
                <XCircle className="h-4 w-4 text-muted-foreground" />
              )}
              <span className={hasSlip ? 'text-green-600' : 'text-muted-foreground'}>
                สลิปเงินเดือน: {hasSlip ? 'สร้างแล้ว' : 'ยังไม่ได้สร้าง'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {hasTavi50 ? (
                <CheckCircle className="h-4 w-4 text-green-500" />
              ) : (
                <XCircle className="h-4 w-4 text-muted-foreground" />
              )}
              <span className={hasTavi50 ? 'text-green-600' : 'text-muted-foreground'}>
                ทวิ 50: {hasTavi50 ? 'สร้างแล้ว' : 'ยังไม่ได้สร้าง'}
              </span>
            </div>
            {salary.generated_slip_at && (
              <div className="text-xs text-muted-foreground mt-2">
                สร้างล่าสุด: {new Date(salary.generated_slip_at).toLocaleString('th-TH')}
              </div>
            )}
            {salary.sent_slip_at && (
              <div className="text-xs text-muted-foreground">
                ส่งอีเมลล่าสุด: {new Date(salary.sent_slip_at).toLocaleString('th-TH')}
              </div>
            )}
          </div>

          {/* Pay Date Input */}
          <div className="space-y-2">
            <Label htmlFor="paidDate">วันที่จ่ายเงินเดือน (dd-MM-yyyy)</Label>
            <Input
              id="paidDate"
              value={paidDate}
              onChange={(e) => setPaidDate(e.target.value)}
              placeholder="เช่น 30-01-2569"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              onClick={handleGenerate}
              disabled={isGenerating}
              className="flex-1"
            >
              {isGenerating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              {hasAnyFile ? 'สร้างสลิปใหม่' : 'สร้างสลิป'}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  disabled={!hasAnyFile || isDownloading}
                >
                  {isDownloading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  ดาวน์โหลด
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem
                  onClick={() => handleDownload('slip')}
                  disabled={!hasSlip}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  สลิปเงินเดือน
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleDownload('tavi50')}
                  disabled={!hasTavi50}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  ทวิ 50
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="outline"
              onClick={handleSendEmail}
              disabled={!hasAnyFile || isSending || !salary.id}
            >
              {isSending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Mail className="h-4 w-4 mr-2" />
              )}
              ส่งอีเมล
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
